import AHDate from "_shared/dates/date";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { type GrantSource, grantSource, kindOfResource, resourceLevel } from "permissions/_views/grant-row/grant-row-model";
import { fieldCounts, isFieldKind, targetIdOf } from "permissions/_views/people/person-record-grants-model";
import { type NameState, personLabel, targetStateText } from "permissions/_views/targets/permission-target-state";
import {
  ACCESS_LEVEL_NAMES,
  FROM,
  HELD,
  SCOPE_NAMES,
  fieldSummary,
  grantExport,
  grantedBy,
  grantedOn,
  kindLabel,
  targetStates,
} from "permissions/_views/wording/permission-wording";
import type Permission from "permissions/permission";

/**
 * What one line of the permissions CSV export says about one grant (#6786, `docs/design/permissions/
 * PERMISSIONS_UI_DESIGN.md`, "CSV export").
 *
 * Plain functions, so the columns and the words in them read and test apart from the reads that
 * gather the grants. Every cell is written with the words a grant row draws: the kind's name, the
 * level and scope names, the source, the target and person states. Dates are ISO days, which a
 * spreadsheet sorts, where a row says "12 Aug".
 */

/** The names an export looked up: the people, their emails, and the records the grants are on. */
export type GrantExportNames = Readonly<{
  /** Each grantee's and grantor's state, keyed by user id in lower case. */
  people: ReadonlyMap<string, NameState>;
  /** The emails of the people the directory found, keyed the same way. */
  emails: ReadonlyMap<string, string>;
  /** Each record's state, keyed by {@link targetKey}. */
  targets: ReadonlyMap<string, NameState>;
}>;

const FAILED: NameState = { state: "failed" };

/** The key of a record in {@link GrantExportNames.targets}: its resource type and its id. */
export function targetKey(resourceType: string, targetId: string): string {
  return `${resourceType}:${targetId.toLowerCase()}`;
}

function personState(names: GrantExportNames, userId: string): NameState {
  return names.people.get(userId.toLowerCase()) ?? FAILED;
}

/** An ISO day, "2026-08-12", or nothing. */
function isoDay(date: AHDate | undefined): string {
  return date ? date.format("YYYY-MM-DD") : "";
}

/**
 * Where the grant came from, as its row says it, without the day: the file has a column for that.
 * A grantor the directory did not name is "Granted", then why there is no name.
 */
export function sourceText(source: GrantSource | undefined, permission: Permission, names: GrantExportNames): string {
  if (!source) return "";
  switch (source.type) {
    case "grantor":
    case "unnamed-grantor": {
      const grantor = personState(names, permission.grantorUserId.value);
      if (grantor.state === "found") return grantedBy(grantor.name, undefined, AHDate.TODAY);
      return [grantedOn(undefined, AHDate.TODAY), personLabel(grantor)].join(" · ");
    }
    case "parents":
      return `${FROM} ${source.parents.map((parent) => parent.label).join(" + ")}`;
    case "system":
    case "roster":
    case "template":
    case "legacy":
      return source.label;
  }
}

/**
 * The record a per-record grant is on, by name, or why it has none: "Conversation no longer exists",
 * "No access to this conversation". A record nobody could name is "Couldn't load this …", as on its
 * row.
 */
function targetText(kind: PermissionKind, permission: Permission, names: GrantExportNames): string {
  const noun = kind.targetNoun ?? "record";
  const targetId = targetIdOf(kind, permission);
  if (!targetId) return "";
  const state = names.targets.get(targetKey(kind.resourceType, targetId)) ?? FAILED;
  return state.state === "found" ? state.name : (targetStateText(state, noun) ?? targetStates.failed(noun));
}

function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The level and scope columns. A kind with neither control is held or not: "Allowed". */
function levelCells(kind: PermissionKind | undefined, permission: Permission): [string, string] {
  if (kind && kind.controls.length === 0) return [HELD, ""];
  const { accessLevel, scope } = resourceLevel(permission.resource);
  return [accessLevel === undefined ? "" : ACCESS_LEVEL_NAMES[accessLevel], scope === undefined ? "" : SCOPE_NAMES[scope]];
}

/** One line of the file, in the order of `grantExport.columns`. */
export function grantExportRow(
  permission: Permission,
  kinds: readonly PermissionKind[],
  names: GrantExportNames,
): string[] {
  const kind = kindOfResource(kinds, permission.resource);
  const grantee = permission.granteeUserId.value;
  const person = personState(names, grantee);
  // The grantor state only decides between the two grantor sources; `sourceText` words both.
  const grantor = personState(names, permission.grantorUserId.value);
  const source = grantSource(permission, kinds, grantor, AHDate.TODAY);
  const [accessLevel, scope] = levelCells(kind, permission);
  const counts = kind && isFieldKind(kind) ? fieldCounts(permission) : undefined;
  return [
    personLabel(person),
    person.state === "found" ? (names.emails.get(grantee.toLowerCase()) ?? "") : "",
    kind?.area ?? "",
    // A kind the kinds endpoint does not describe is still a grant somebody holds: named by its type.
    kind ? kindLabel(kind) : permission.resource.resourceType,
    kind?.perRecord ? sentenceCase(kind.targetNoun ?? "record") : "",
    kind?.perRecord ? targetText(kind, permission, names) : "",
    accessLevel,
    scope,
    counts ? fieldSummary(counts.granted, counts.total, counts.writable) : "",
    permission.isDisabled ? grantExport.states.removed : grantExport.states.enabled,
    sourceText(source, permission, names),
    isoDay(permission.createdDate),
    permission.isDisabled ? isoDay(permission.disabledDate) : "",
  ];
}

/**
 * The order of the file's lines: by person, then area and kind in the kinds endpoint's order, then
 * record, live before removed, oldest first. A person page's file reads area by area, a record
 * page's person by person, and a kind page's person by person, record by record.
 */
export function sortGrantExportRows(
  rows: readonly (readonly string[])[],
  kinds: readonly PermissionKind[],
): string[][] {
  const kindOrder = new Map(kinds.map((kind, index) => [kindLabel(kind), index]));
  const areaOrder = new Map([...new Set(kinds.map((kind) => kind.area))].map((area, index) => [area, index]));
  const [PERSON, , AREA, KIND, , TARGET, , , , STATE, , GRANTED] = grantExport.columns.map((_, index) => index);
  const compareText = (left: string, right: string) => left.localeCompare(right, undefined, { sensitivity: "base" });
  return [...rows]
    .map((row) => [...row])
    .sort(
      (left, right) =>
        compareText(left[PERSON], right[PERSON]) ||
        (areaOrder.get(left[AREA]) ?? Number.MAX_SAFE_INTEGER) - (areaOrder.get(right[AREA]) ?? Number.MAX_SAFE_INTEGER) ||
        (kindOrder.get(left[KIND]) ?? Number.MAX_SAFE_INTEGER) - (kindOrder.get(right[KIND]) ?? Number.MAX_SAFE_INTEGER) ||
        compareText(left[TARGET], right[TARGET]) ||
        compareText(left[STATE], right[STATE]) ||
        left[GRANTED].localeCompare(right[GRANTED]),
    );
}

/**
 * One cell, quoted where it holds a comma, a quote or a line break. A cell that starts as a
 * spreadsheet formula would (`=`, `+`, `-`, `@`, a tab or a return) is led by an apostrophe, so a
 * record or person named `=HYPERLINK(…)` opens as text and never runs.
 */
export function csvCell(value: string): string {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replaceAll('"', '""')}"` : safe;
}

/**
 * The whole file: the headings, then a line per row, each ended with CRLF as RFC 4180 has it. A byte
 * order mark leads, so a spreadsheet opens a name with an accent as that name.
 */
export function grantExportCsv(rows: readonly (readonly string[])[]): string {
  return `﻿${[grantExport.columns, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n")}\r\n`;
}

/** The file's name: "permissions-dana-ruiz-2026-10-20.csv". */
export function grantExportFileName(subject: string, today: AHDate = AHDate.TODAY): string {
  const slug = subject
    .normalize("NFKD")
    .replaceAll(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replaceAll(/^-+|-+$/g, "");
  return `permissions-${slug ? `${slug}-` : ""}${isoDay(today)}.csv`;
}
